import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"

interface PrivacyPolicyProps {
  isOpen: boolean
  onClose: () => void
}

export function PrivacyPolicy({ isOpen, onClose }: PrivacyPolicyProps) {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold">
            Privacy Policy
          </DialogTitle>
          <DialogDescription>
            Last updated: {new Date().toLocaleDateString()}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-6 text-sm leading-relaxed">
          <section>
            <h3 className="text-lg font-semibold mb-3">1. Information We Collect</h3>
            <p className="mb-3">
              We collect information you provide directly to us, such as when you create an account, use our services, or contact us for support.
            </p>
            <p className="mb-3">The types of information we collect include:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Account information (name, email address, profile picture)</li>
              <li>Authentication data from third-party providers (Google, GitHub, Microsoft)</li>
              <li>Database connection information and configurations</li>
              <li>Usage data and analytics</li>
              <li>Communication records when you contact us</li>
            </ul>
          </section>

          <section>
            <h3 className="text-lg font-semibold mb-3">2. How We Use Your Information</h3>
            <p className="mb-3">We use the information we collect to:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Provide, maintain, and improve our services</li>
              <li>Process transactions and send related information</li>
              <li>Send technical notices, updates, security alerts, and support messages</li>
              <li>Respond to your comments, questions, and customer service requests</li>
              <li>Monitor and analyze trends, usage, and activities in connection with our services</li>
              <li>Detect, investigate, and prevent fraudulent transactions and other illegal activities</li>
            </ul>
          </section>

          <section>
            <h3 className="text-lg font-semibold mb-3">3. Information Sharing</h3>
            <p className="mb-3">
              We do not sell, trade, or otherwise transfer your personal information to third parties without your consent, except in the following circumstances:
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>With your explicit consent</li>
              <li>To comply with legal obligations</li>
              <li>To protect our rights, property, or safety</li>
              <li>In connection with a business transfer or merger</li>
              <li>With service providers who assist us in operating our services</li>
            </ul>
          </section>

          <section>
            <h3 className="text-lg font-semibold mb-3">4. Data Security</h3>
            <p className="mb-3">
              We implement appropriate technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction.
            </p>
            <p>
              However, no method of transmission over the internet or electronic storage is 100% secure, and we cannot guarantee absolute security.
            </p>
          </section>

          <section>
            <h3 className="text-lg font-semibold mb-3">5. Data Retention</h3>
            <p>
              We retain your personal information for as long as necessary to provide our services, comply with legal obligations, resolve disputes, and enforce our agreements. When we no longer need your personal information, we will securely delete or anonymize it.
            </p>
          </section>

          <section>
            <h3 className="text-lg font-semibold mb-3">6. Your Rights</h3>
            <p className="mb-3">Depending on your location, you may have the following rights regarding your personal information:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Access and receive a copy of your personal information</li>
              <li>Correct inaccurate or incomplete information</li>
              <li>Delete your personal information</li>
              <li>Restrict or object to processing</li>
              <li>Data portability</li>
              <li>Withdraw consent where processing is based on consent</li>
            </ul>
          </section>

          <section>
            <h3 className="text-lg font-semibold mb-3">7. Cookies and Tracking Technologies</h3>
            <p className="mb-3">
              We use cookies and similar tracking technologies to enhance your experience, analyze usage, and provide personalized content.
            </p>
            <p>
              You can control cookie settings through your browser preferences, though disabling cookies may affect the functionality of our services.
            </p>
          </section>

          <section>
            <h3 className="text-lg font-semibold mb-3">8. Third-Party Services</h3>
            <p>
              Our services may contain links to third-party websites or integrate with third-party services. We are not responsible for the privacy practices of these third parties. We encourage you to review their privacy policies before providing any personal information.
            </p>
          </section>

          <section>
            <h3 className="text-lg font-semibold mb-3">9. Children's Privacy</h3>
            <p>
              Our services are not intended for children under the age of 13. We do not knowingly collect personal information from children under 13. If you believe we have collected information from a child under 13, please contact us immediately.
            </p>
          </section>

          <section>
            <h3 className="text-lg font-semibold mb-3">10. International Data Transfers</h3>
            <p>
              Your information may be transferred to and processed in countries other than your own. We ensure that such transfers comply with applicable data protection laws and implement appropriate safeguards to protect your information.
            </p>
          </section>

          <section>
            <h3 className="text-lg font-semibold mb-3">11. Changes to This Policy</h3>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date. We encourage you to review this Privacy Policy periodically.
            </p>
          </section>

          <section>
            <h3 className="text-lg font-semibold mb-3">12. Contact Us</h3>
            <p>
              If you have any questions about this Privacy Policy or our privacy practices, please contact us at privacy@quickdb.com
            </p>
          </section>
        </div>
      </DialogContent>
    </Dialog>
  )
} 